/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ReviewQuestionModal
 *
 * @file  ReviewQuestionModal.jsx
 * @author Anuja Gaikwad
 * @since 1.0.0
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  TextareaAutosize,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';

import useStyles from './styles/ReviewQuestionsStyle';
import Icons from '../icons/Icon';
import CheckboxOutlineIcon from '../icons/CheckboxOutlineIcon';
import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon';
import { BadgeTemplate } from '../tabular/components/Template';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';

const ReviewQuestionModal = (props) => {
  const { open, onClose, reviewData, onInaccurateClick, textInfo, reportQuestionReasons } = props;
  const classes = useStyles(props);

  const [expandedComments, setExpandedComments] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [comment, setComment] = useState('');

  if (!reviewData) return null;

  const handleCheckboxChange = (item) => {
    setSelectedReasons((prev) => {
      const alreadySelected = prev.includes(item.id);

      if (alreadySelected) {
        return prev.filter((id) => id !== item.id);
      }

      return [...prev, item.id];
    });
  };


  const handleSubmit = () => {
    // gets the correct selected checkbox and passes to callback.
    const reasonForDisLike = selectedReasons.map((id) => {
      const reason = reportQuestionReasons.find((r) => r.id === id);

      return reason != null ? reason.value : id;
    });
    onInaccurateClick({
      questionId: reviewData.question.id,
      comments: comment,
      reasonForDisLike,
      ...(reviewData.feedbackSubmit || {})
    });

    // Optional reset after submit
    setSelectedReasons([]);
    setComment('');
    onClose();
  };

  const resetForm = () => {
    setSelectedReasons([]);
    setComment('');
    onClose();
  };

  const formatTopic = (key) => {
  // Insert space before uppercase letters, lowercase everything, then capitalize first letter
    const result = key
      .replace(/([A-Z])/g, ' $1') // add space before uppercase
      .toLowerCase() // make everything lowercase
      .trim(); // remove leading space if any

    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  const formatStatus = (key) => key.toLowerCase(); // notRelevant -> notrelevant

  const reportBreakdown = reviewData?.reportBreakdown || {};
  const hasReportBreakdown = Object.values(reportBreakdown).some(
    (count) => Number(count) > 0
  );
  const hasStudentComments = (reviewData.studentComments?.length ?? 0) > 0;
  const showStudentReportsSection =
    Number(reviewData.totalStudentReports) > 0
    || hasReportBreakdown
    || hasStudentComments;

  const canSubmitReport =
    selectedReasons.length > 0 || comment.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      {/* Close Icon */}
      <IconButton className={classes.closeButton} onClick={onClose}>
        <Icons name="CloseIcon" />
      </IconButton>

      <div className={classes.container}>
        {/* Header */}
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.title}>
            {textInfo.reportQuestionTitle}
          </Box>
        </DialogTitle>

        <Box className={classes.question}>
          {textInfo.reportQuestionLabel}
        </Box>

        {/* Body */}
        <DialogContent className={classes.dialogContent}>
          <div className={classes.contentRow}>

            {/* Checkbox Section */}
            <Box className={classes.checkboxGroup}>
              {reportQuestionReasons?.map((item) => (
                <Box key={item.id} className={classes.checkboxRow}>
                  <Checkbox
                    color="primary"
                    disableRipple
                    checked={selectedReasons.includes(item.id)}
                    onChange={() => handleCheckboxChange(item)}
                    className={classes.customCheckbox}
                    icon={
                      <CheckboxOutlineIcon
                        className={classes.checkboxIcon}
                      />
                    }
                    checkedIcon={
                      <CheckboxCheckedIcon
                        className={classes.checkboxIcon}
                      />
                    }
                  />
                  <Typography className={classes.checkboxLabel}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Textarea */}
            <TextareaAutosize
              className={classes.textArea}
              minRows={4}
              placeholder={textInfo.placeholderText}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </DialogContent>

        {/* Student Reports Section */}
        {showStudentReportsSection && (
          <div>
            <div className={classes.studentReportLabel}>
              {reviewData.totalStudentReports} {textInfo.studentReports}
            </div>

            <div className={classes.badgeTemplateContainer}>
              {Object.entries(reportBreakdown).map(([key, count]) => (
                <BadgeTemplate
                  key={key}
                  topics={[formatTopic(key)]}
                  status={formatStatus(key)}
                  count={count}
                />
              ))}
            </div>

            <Accordion
              expanded={expandedComments}
              onChange={() => setExpandedComments(!expandedComments)}
              elevation={0}
              classes={{
                root: classes.accordionRoot
              }}
            >
              <AccordionSummary
                expandIcon={
                  <Icons
                    name="ExpandIcon"
                    style={{ width: 16, height: 9 }}
                  />
                }
                classes={{
                  root: classes.accordionSummaryRoot,
                  content: classes.accordionSummaryContent,
                  expanded: classes.accordionSummaryExpanded
                }}
                aria-controls="student-comments-content"
                id="student-comments-header"
              >
                <Typography className={classes.commentsTitle}>
                  {textInfo.showComments}
                </Typography>
              </AccordionSummary>

              <AccordionDetails classes={{ root: classes.accordionDetailsRoot }}>
                <div className={classes.commentsContainer}>
                  <CustomScrollbar className={classes.scrollArea}>
                    {reviewData.studentComments?.map((comments, idx) => (
                      <Typography
                        key={idx}
                        className={classes.commentsItems}
                      >
                        {comments}
                      </Typography>
                    ))}
                  </CustomScrollbar>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        )}

        {/* Footer */}
        <DialogActions classes={{ root: classes.dialogActions }}>
          <div className={classes.footer}>
            <Button
              variant="outlined"
              className={classes.disregards}
              onClick={resetForm}
            >
              {textInfo.cancelButtonLabel}
            </Button>

            <Button
              variant="contained"
              className={classes.reportInaccurate}
              onClick={handleSubmit}
              disabled={!canSubmitReport}
            >
              {textInfo.reportAsInaccurateButtonText}
            </Button>
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
};

ReviewQuestionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInaccurateClick: PropTypes.func.isRequired,
  reviewData: PropTypes.object.isRequired,
  reportQuestionReasons: PropTypes.array.isRequired,
  textInfo: PropTypes.shape({
    commentsFromStudentLabel: PropTypes.string,
    cancelButtonLabel: PropTypes.string,
    reportAsInaccurateButtonText: PropTypes.string,
    flaggedText: PropTypes.string,
    showComments: PropTypes.string,
    studentReports: PropTypes.string,
    placeholderText: PropTypes.string,
    reportQuestionTitle: PropTypes.string,
    reportQuestionLabel: PropTypes.string
  }).isRequired
};

export default ReviewQuestionModal;
